import React, { useMemo } from 'react';
import AppLayout from '../components/AppLayout';
import MonthlySummaryCard from '../components/crm/MonthlySummaryCard';
import BacklogCounts from '../components/crm/BacklogCounts';
import LeadTimeMetric from '../components/crm/LeadTimeMetric';
import SoftWarnings from '../components/crm/SoftWarnings';
import StatusDistributionChart from '../components/crm/StatusDistributionChart';
import TrendChart from '../components/crm/TrendChart';
import { useJobs } from '../hooks/useJobs';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { daysBetween } from '../utils/date-utils';
import { getEstimates } from '../services/estimate-service';
import { useState, useEffect } from 'react';
import { DollarSign, CheckCircle2, Clock, Activity } from 'lucide-react';

const INK    = '#2B2620';
const WALNUT = '#8B4513';
const MOSS   = '#3B6B44';
const OCHRE  = '#A6752A';
const RUST   = '#A13D2D';
const STONE  = '#8C8275';
const PAPER  = '#FDFBF8';

const FONT_DISPLAY = "'Fraunces', Georgia, serif";
const FONT_SANS    = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

// Robust date parser — handles Firestore Timestamps, ISO strings, Date objects.
// Returns null if the input is missing/unparseable so the filter logic stays clean.
function parseDate(v) {
  if (!v) return null;
  // Firestore Timestamp
  if (typeof v.toDate === 'function') return v.toDate();
  // Already a Date
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  // String or number
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

export default function CRMDashboardPage() {
  const { jobs, loading } = useJobs();
  const [estimates, setEstimates] = useState([]);

  useEffect(() => {
    getEstimates().then(setEstimates).catch(err => {
      console.error('Failed to load estimates for dashboard:', err);
      setEstimates([]);
    });
  }, []);

  // Calculate metrics
  const metrics = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();


    // Sold This Month = approved estimates this month.
    // In this app, sales rep approval IS the sold event (contracts are physical).
    // Falls back to updatedAt for legacy approved estimates without approvedAt yet.
    const soldThisMonth = estimates.filter(est => {
      if (est.status !== 'approved') return false;
      const approvedAt = parseDate(est.approvedAt) || parseDate(est.updatedAt);
      if (!approvedAt) return false;
      return approvedAt.getMonth() === currentMonth && approvedAt.getFullYear() === currentYear;
    }).length;

    // This month's completed jobs.
    // Prefer the statusHistory "completed" entry (most authoritative — set by the
    // installer completion flow), then completedAt (also set there), then fall back
    // to updatedAt — the same fallback chain used for soldThisMonth above. This
    // matters because jobs completed from the office-side Jobs Database page only
    // ever get status + updatedAt written (no statusHistory entry), so requiring
    // statusHistory alone silently excluded most completions.
    const completedThisMonth = jobs.filter(job => {
      if (job.status !== 'completed') return false;
      const completedEntry = job.statusHistory?.find(h => h.status === 'completed');
      const completedDate = parseDate(completedEntry?.timestamp) || parseDate(job.completedAt) || parseDate(job.updatedAt);
      if (!completedDate) return false;
      return completedDate.getMonth() === currentMonth &&
             completedDate.getFullYear() === currentYear;
    }).length;

    // Unscheduled jobs count — excludes completed, cancelled, and on_hold.
    // On hold jobs are tracked separately so they don't bloat this number.
    const unscheduledCount = jobs.filter(j =>
      !j.scheduledDate &&
      j.status !== 'completed' &&
      j.status !== 'cancelled' &&
      j.status !== 'on_hold'
    ).length;

    // On Hold jobs count
    const onHoldCount = jobs.filter(j => j.status === 'on_hold').length;

    // Status counts
    const statusCounts = {
      sold: jobs.filter(j => j.status === 'sold').length,
      awaiting_production: jobs.filter(j => j.status === 'awaiting_production').length,
      in_production: jobs.filter(j => j.status === 'in_production').length,
      ready_for_install: jobs.filter(j => j.status === 'ready_for_install').length,
      scheduled: jobs.filter(j => j.status === 'scheduled').length,
      on_hold: onHoldCount,
      completed: jobs.filter(j => j.status === 'completed').length
    };

    // Average lead time (sold to scheduled)
    const scheduledJobs = jobs.filter(j => j.soldDate && j.scheduledDate);
    const avgLeadTime = scheduledJobs.length > 0
      ? scheduledJobs.reduce((sum, job) => {
          return sum + daysBetween(job.soldDate, job.scheduledDate);
        }, 0) / scheduledJobs.length
      : 0;

    // Warnings
    const warnings = [];
    
    // On Hold jobs warning
    if (onHoldCount > 0) {
      warnings.push({
        type: 'on_hold',
        title: `${onHoldCount} job${onHoldCount !== 1 ? 's' : ''} on hold`,
        message: 'These jobs need attention before they can be rescheduled'
      });
    }
    
    // Old unscheduled jobs (>30 days)
    const oldUnscheduled = jobs.filter(job => {
      if (job.scheduledDate || !job.soldDate) return false;
      if (job.status === 'completed' || job.status === 'cancelled' || job.status === 'on_hold') return false;
      const daysSinceSold = daysBetween(job.soldDate, new Date());
      return daysSinceSold > 30;
    });

    if (oldUnscheduled.length > 0) {
      warnings.push({
        type: 'old_unscheduled',
        title: `${oldUnscheduled.length} job${oldUnscheduled.length !== 1 ? 's' : ''} unscheduled >30 days`,
        message: 'These jobs have been waiting for scheduling for over a month',
        job: oldUnscheduled[0]
      });
    }

    // Long lead time warning
    if (avgLeadTime > 45) {
      warnings.push({
        type: 'long_lead_time',
        title: 'Lead time is high',
        message: `Average lead time is ${Math.round(avgLeadTime)} days. Consider scheduling jobs sooner.`
      });
    }

    // Status distribution for chart
    const statusDistribution = [
      { status: 'sold', label: 'Sold', count: statusCounts.sold },
      { status: 'awaiting_production', label: 'Awaiting Production', count: statusCounts.awaiting_production },
      { status: 'in_production', label: 'In Production', count: statusCounts.in_production },
      { status: 'ready_for_install', label: 'Ready for Install', count: statusCounts.ready_for_install },
      { status: 'scheduled', label: 'Scheduled', count: statusCounts.scheduled },
      { status: 'on_hold', label: 'On Hold', count: statusCounts.on_hold }
    ].filter(item => item.count > 0);

    // Trend data (last 6 months)
    const months = [];
    const soldData = [];
    const completedData = [];

    for (let i = 5; i >= 0; i--) {
      const d = new Date(currentYear, currentMonth - i, 1);
      const monthName = d.toLocaleDateString('en-US', { month: 'short' });
      months.push(monthName);

      const sold = estimates.filter(est => {
        if (est.status !== 'approved') return false;
        const approvedAt = parseDate(est.approvedAt) || parseDate(est.updatedAt);
        if (!approvedAt) return false;
        return approvedAt.getMonth() === d.getMonth() && approvedAt.getFullYear() === d.getFullYear();
      }).length;

      // Completed count for trend — same statusHistory → completedAt → updatedAt
      // fallback chain as completedThisMonth above.
      const completed = jobs.filter(job => {
        if (job.status !== 'completed') return false;
        const completedEntry = job.statusHistory?.find(h => h.status === 'completed');
        const completedDate = parseDate(completedEntry?.timestamp) || parseDate(job.completedAt) || parseDate(job.updatedAt);
        if (!completedDate) return false;
        return completedDate.getMonth() === d.getMonth() &&
               completedDate.getFullYear() === d.getFullYear();
      }).length;

      soldData.push(sold);
      completedData.push(completed);
    }

    return {
      soldThisMonth,
      completedThisMonth,
      unscheduledCount,
      onHoldCount,
      statusCounts,
      avgLeadTime,
      warnings,
      statusDistribution,
      trendMonths: months,
      trendSold: soldData,
      trendCompleted: completedData
    };
  }, [jobs, estimates]);

  const breadcrumb = 'Dashboard › CRM Dashboard';

  if (loading) {
    return (
      <AppLayout title="CRM Dashboard" breadcrumb={breadcrumb}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '400px'
        }}>
          <LoadingSpinner size="large" color={WALNUT} />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title="CRM Dashboard"
      breadcrumb={breadcrumb}
    >
      <div style={{ fontFamily: FONT_SANS }}>

        {/* Header */}
        <div style={{ background: PAPER, borderRadius: '16px', padding: '20px 24px', border: '1px solid rgba(139,69,19,0.1)', boxShadow: '0 2px 12px rgba(139,69,19,0.06)', marginBottom: '24px' }}>
          <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: '24px', fontWeight: 600, color: INK, margin: '0 0 4px 0' }}>
            CRM Dashboard
          </h2>
          <p style={{ fontSize: '13px', color: STONE, margin: 0 }}>
            Pipeline health, lead times, and month-over-month trends
          </p>
        </div>

        {/* KPI Summary Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '20px',
          marginBottom: '24px'
        }}>
          <MonthlySummaryCard
            title="Sold This Month"
            value={metrics.soldThisMonth}
            subtitle="Estimates approved"
            Icon={DollarSign}
            color={OCHRE}
          />

          <MonthlySummaryCard
            title="Completed This Month"
            value={metrics.completedThisMonth}
            subtitle="Jobs finished"
            Icon={CheckCircle2}
            color={MOSS}
          />

          <MonthlySummaryCard
            title="Unscheduled Jobs"
            value={metrics.unscheduledCount}
            subtitle="Need scheduling"
            Icon={Clock}
            color={RUST}
          />

          <MonthlySummaryCard
            title="Active Jobs"
            value={metrics.statusCounts.scheduled + metrics.statusCounts.in_production + metrics.statusCounts.ready_for_install}
            subtitle="In production or scheduled"
            Icon={Activity}
            color={WALNUT}
          />
        </div>

        {/* Main Content Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: '24px',
          marginBottom: '24px'
        }}>
          <BacklogCounts counts={metrics.statusCounts} />

          <LeadTimeMetric
            averageDays={Math.round(metrics.avgLeadTime)}
            trend={null}
          />
        </div>

        {/* Warnings */}
        <div style={{ marginBottom: '24px' }}>
          <SoftWarnings warnings={metrics.warnings} />
        </div>

        {/* Charts */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: '24px'
        }}>
          <StatusDistributionChart data={metrics.statusDistribution} />

          <TrendChart
            soldData={metrics.trendSold}
            completedData={metrics.trendCompleted}
            months={metrics.trendMonths}
          />
        </div>
      </div>
    </AppLayout>
  );
}